import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FadeUp, Stagger, StaggerItem } from '../components/Motion';
import { Clock, Users, Tag, Check, ArrowRight, X, Send, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/3yw249cq_IMG_7085.jpeg';
const CATEGORIES = ['all', 'visit', 'workshop', 'immersion', 'corporate'];

export default function Experiences() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);

  useEffect(() => { api.get('/experiences').then((r) => setItems(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(
    () => filter === 'all' ? items : items.filter((e) => e.category === filter),
    [items, filter]
  );

  return (
    <main data-testid="experiences-page" className="pt-0">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover animate-slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-transparent" />
        </div>
        <div className="relative container-x max-w-7xl mx-auto pb-24 pt-40">
          <FadeUp><p className="overline text-sun mb-6">{t('experiences.overline')}</p></FadeUp>
          <FadeUp delay={0.1}>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-bone leading-[0.95] tracking-tight whitespace-pre-line text-balance max-w-5xl">
              {t('experiences.title')}
            </h1>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 text-lg lg:text-xl text-bone/85 max-w-2xl leading-relaxed">{t('experiences.sub')}</p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#offerings" className="btn-accent" data-testid="cta-offerings">
                {t('experiences.cta_explore')} <ArrowRight size={18} />
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="section bg-bone">
        <div className="container-x max-w-7xl mx-auto grid gap-12 lg:grid-cols-12 items-start">
          <FadeUp className="lg:col-span-5">
            <p className="overline mb-3">{t('experiences.principles_overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight">{t('experiences.principles_title')}</h2>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-6 lg:col-start-7">
            <ul className="grid gap-4">
              {t('experiences.principles', { returnObjects: true }).map((p) => (
                <li key={p} className="flex items-start gap-3 border-b border-border-soft pb-4">
                  <CheckCircle2 size={22} className="text-forest mt-0.5 shrink-0" />
                  <span className="text-ink-soft text-lg">{p}</span>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </section>

      {/* OFFERINGS */}
      <section id="offerings" className="section bg-sand/40 scroll-mt-24">
        <div className="container-x max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <FadeUp>
              <p className="overline mb-3">{t('experiences.offerings_overline')}</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight">{t('experiences.offerings_title')}</h2>
            </FadeUp>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`exp-filter-${c}`}
                className={`text-sm px-4 py-2 rounded-full border transition-all ${
                  filter === c ? 'bg-forest text-bone border-forest' : 'bg-white text-ink-soft border-border-soft hover:border-forest hover:text-forest'
                }`}
              >
                {t(`experiences.categories.${c}`)}
              </button>
            ))}
          </div>

          <Stagger className="grid gap-8 md:grid-cols-2 lg:grid-cols-2">
            {filtered.map((e) => (
              <StaggerItem key={e.id}>
                <article
                  className="bg-white rounded-3xl overflow-hidden border border-border-soft hover-lift h-full flex flex-col"
                  data-testid={`experience-${e.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={resolveImg(e.image)} alt={e[`title_${lng}`]} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-earth border border-earth/30 rounded-full px-2 py-0.5">
                        {t(`experiences.categories.${e.category}`)}
                      </span>
                    </div>
                    <h3 className="font-heading text-2xl sm:text-3xl text-ink leading-tight">{e[`title_${lng}`]}</h3>
                    <p className="mt-3 text-ink-soft leading-relaxed">{e[`summary_${lng}`]}</p>
                    <div className="mt-5 grid gap-2 text-sm text-ink-soft">
                      {e.duration && <span className="flex items-center gap-2"><Clock size={14} className="text-forest" /> {e.duration}</span>}
                      {e.capacity && <span className="flex items-center gap-2"><Users size={14} className="text-forest" /> {e.capacity}</span>}
                      {e.price && <span className="flex items-center gap-2"><Tag size={14} className="text-forest" /> {e.price}</span>}
                    </div>
                    <div className="mt-6 pt-5 border-t border-border-soft flex items-center gap-3">
                      <button
                        onClick={() => setActive({ ...e, mode: 'details' })}
                        className="text-forest text-sm font-medium border-b border-forest/40 pb-0.5 hover:border-forest"
                        data-testid={`exp-details-${e.id}`}
                      >
                        {t('experiences.details')}
                      </button>
                      <button
                        onClick={() => setActive({ ...e, mode: 'book' })}
                        className="ml-auto btn-primary !px-5 !py-2 text-sm"
                        data-testid={`exp-book-${e.id}`}
                      >
                        {t('experiences.book')} <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-forest text-bone">
        <div className="container-x max-w-7xl mx-auto grid gap-10 lg:grid-cols-12 items-center">
          <FadeUp className="lg:col-span-7">
            <p className="overline text-sun mb-3">{t('experiences.custom_overline')}</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t('experiences.custom_title')}</h2>
            <p className="mt-5 text-bone/85 text-lg leading-relaxed max-w-2xl">{t('experiences.custom_text')}</p>
          </FadeUp>
          <FadeUp delay={0.1} className="lg:col-span-4 lg:col-start-9">
            <button onClick={() => setActive({ mode: 'book', title_fr: '', title_en: '' })} className="btn-accent w-full" data-testid="cta-custom-book">
              {t('experiences.custom_cta')} <ArrowRight size={18} />
            </button>
          </FadeUp>
        </div>
      </section>

      {active && (
        <Modal experience={active} onClose={() => setActive(null)} lng={lng} />
      )}
    </main>
  );
}

function Modal({ experience, onClose, lng }) {
  const { t } = useTranslation();
  const isBook = experience.mode === 'book';
  const [form, setForm] = useState({
    name: '', email: '', phone: '', organization: '', country: '',
    group_size: 2, preferred_dates: '', message: '',
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState(experience.mode || 'details');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api.post('/experience-bookings', {
        experience_id: experience.id || '',
        experience_title: experience[`title_${lng}`] || experience.title_fr || 'Sur-mesure',
        ...form,
        group_size: parseInt(form.group_size, 10) || 1,
      });
      setSent(true);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === 'string' ? d : t('experiences.error'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-ink/80 backdrop-blur-sm grid place-items-center p-4 overflow-auto" onClick={onClose} data-testid="experience-modal">
      <div className="bg-bone max-w-3xl w-full rounded-3xl overflow-hidden my-12" onClick={(e) => e.stopPropagation()}>
        {experience.image && (
          <div className="aspect-[16/8] overflow-hidden">
            <img src={resolveImg(experience.image)} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-8 sm:p-12">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div>
              {experience.category && (
                <span className="overline text-earth">{t(`experiences.categories.${experience.category}`)}</span>
              )}
              <h2 className="mt-2 font-heading text-3xl sm:text-4xl text-ink">
                {experience[`title_${lng}`] || t('experiences.custom_title')}
              </h2>
            </div>
            <button onClick={onClose} className="text-ink-muted hover:text-ink shrink-0" data-testid="modal-close"><X /></button>
          </div>

          {experience.id && (
            <div className="mt-5 flex flex-wrap gap-3 text-sm text-ink-soft">
              {experience.duration && <span className="flex items-center gap-2"><Clock size={14} className="text-forest" /> {experience.duration}</span>}
              {experience.capacity && <span className="flex items-center gap-2"><Users size={14} className="text-forest" /> {experience.capacity}</span>}
              {experience.price && <span className="flex items-center gap-2"><Tag size={14} className="text-forest" /> {experience.price}</span>}
            </div>
          )}

          {experience.id && (
            <div className="mt-6 flex gap-2 border-b border-border-soft">
              <button
                onClick={() => setTab('details')}
                className={`px-4 py-2 text-sm border-b-2 ${tab === 'details' ? 'border-forest text-forest' : 'border-transparent text-ink-muted'}`}
                data-testid="modal-tab-details"
              >
                {t('experiences.details')}
              </button>
              <button
                onClick={() => setTab('book')}
                className={`px-4 py-2 text-sm border-b-2 ${tab === 'book' ? 'border-forest text-forest' : 'border-transparent text-ink-muted'}`}
                data-testid="modal-tab-book"
              >
                {t('experiences.book')}
              </button>
            </div>
          )}

          {tab === 'details' && experience.id && (
            <div className="mt-6">
              <p className="text-ink-soft leading-relaxed whitespace-pre-line">{experience[`description_${lng}`]}</p>
              {(experience[`includes_${lng}`] || []).length > 0 && (
                <div className="mt-6">
                  <p className="overline mb-2">{t('experiences.includes')}</p>
                  <ul className="grid gap-2">
                    {experience[`includes_${lng}`].map((it) => (
                      <li key={it} className="flex items-start gap-2 text-ink-soft"><Check size={16} className="text-forest mt-1 shrink-0" /> {it}</li>
                    ))}
                  </ul>
                </div>
              )}
              {experience[`audience_${lng}`] && (
                <div className="mt-6">
                  <p className="overline mb-2">{t('experiences.audience')}</p>
                  <p className="text-ink-soft">{experience[`audience_${lng}`]}</p>
                </div>
              )}
              <button onClick={() => setTab('book')} className="mt-8 btn-primary" data-testid="modal-go-book">
                {t('experiences.book')} <ArrowRight size={16} />
              </button>
            </div>
          )}

          {tab === 'book' && (sent ? (
            <div className="mt-8 text-center" data-testid="booking-success">
              <div className="w-14 h-14 grid place-items-center rounded-full bg-forest/10 text-forest mx-auto mb-4">
                <CheckCircle2 size={28} />
              </div>
              <h3 className="font-heading text-2xl text-ink">{t('experiences.sent_title')}</h3>
              <p className="text-ink-soft mt-2">{t('experiences.sent_sub')}</p>
              <button onClick={onClose} className="mt-6 btn-outline" data-testid="booking-success-close">{t('common.close')}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t('experiences.f_name')} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="bk-name" />
                <Field label={t('experiences.f_email')} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="bk-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t('experiences.f_phone')} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="bk-phone" />
                <Field label={t('experiences.f_country')} value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="bk-country" />
              </div>
              <Field label={t('experiences.f_organization')} value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="bk-organization" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t('experiences.f_group_size')} type="number" value={form.group_size} onChange={(v) => setForm({ ...form, group_size: v })} required testid="bk-group" />
                <Field label={t('experiences.f_dates')} value={form.preferred_dates} onChange={(v) => setForm({ ...form, preferred_dates: v })} testid="bk-dates" />
              </div>
              <div className="grid gap-2">
                <label className="overline">{t('experiences.f_message')}</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="bk-message"
                  className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm resize-none"
                />
              </div>
              {err && <p className="text-red-700 text-sm" data-testid="booking-error">{err}</p>}
              <button type="submit" disabled={busy} className="btn-primary self-start mt-2 disabled:opacity-60" data-testid="booking-submit">
                {busy ? '…' : <>{t('experiences.submit')} <Send size={16} /></>}
              </button>
            </form>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = 'text', value, onChange, required, testid }) {
  return (
    <div className="grid gap-2">
      <label className="overline">{label}</label>
      <input
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm"
      />
    </div>
  );
}
